import React, { useEffect, useState } from "react";

import { Loader2, PlusIcon, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { ImageUpload } from "~/components/ui/image-upload";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { UNIT_OPTIONS } from "~/constants/units";
import { uploadProductImage } from "~/services/productImageService";
import { PriceRange, Product } from "~/types/product";

interface ProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  onSave: (product: Product) => void;
  saving?: boolean;
}

export const ProductDialog: React.FC<ProductDialogProps> = ({
  open,
  onOpenChange,
  product,
  onSave,
  saving = false,
}) => {
  const [formData, setFormData] = useState<Omit<Product, "id" | "created_at">>({
    product_image_url: "",
    short_name: "",
    long_name: "",
    description: "",
    price_ranges: [{ min: 0, max: 100, price: 0 }],
    stock: 0,
    sku: "",
    barcode: "",
    unit_code: "",
    base_measurement_unit: "",
    group_code: "",
    group_description: "",
    subgroup_code: "",
    subgroup_description: "",
    visible_to_sellers: true,
    discount_percent: 0,
    weight_kg: undefined,
    volume_cm3: undefined,
    updated_at: new Date().toISOString(),
  });

  // State for image upload
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string>("");

  // Set form data when product is provided (edit mode)
  useEffect(() => {
    if (product) {
      console.log("Product data received in dialog:", product);
      console.log("SKU value:", product.sku);

      setFormData({
        product_image_url: product.product_image_url || "",
        short_name: product.short_name,
        long_name: product.long_name || "",
        description: product.description || "",
        price_ranges:
          product.price_ranges?.length > 0
            ? [...product.price_ranges].sort((a, b) => a.min - b.min)
            : [{ min: 0, max: 100, price: 0 }],
        stock: product.stock,
        sku: product.sku || "",
        barcode: product.barcode || "",
        unit_code: product.unit_code || "",
        base_measurement_unit: product.base_measurement_unit || "",
        group_code: product.group_code || "",
        group_description: product.group_description || "",
        subgroup_code: product.subgroup_code || "",
        subgroup_description: product.subgroup_description || "",
        visible_to_sellers: product.visible_to_sellers ?? true,
        discount_percent: product.discount_percent ?? 0,
        weight_kg: product.weight_kg,
        volume_cm3: product.volume_cm3,
        updated_at: new Date().toISOString(),
      });

      // Debug log after setting form data
      console.log("Form data after setting:", formData);
    } else {
      // Reset form data for new product
      setFormData({
        product_image_url: "",
        short_name: "",
        long_name: "",
        description: "",
        price_ranges: [{ min: 0, max: 100, price: 0 }],
        stock: 0,
        sku: "",
        barcode: "",
        unit_code: "",
        base_measurement_unit: "",
        group_code: "",
        group_description: "",
        subgroup_code: "",
        subgroup_description: "",
        visible_to_sellers: true,
        discount_percent: 0,
        weight_kg: undefined,
        volume_cm3: undefined,
        updated_at: new Date().toISOString(),
      });
    }

    // Reset image upload state when dialog opens/closes or product changes
    setSelectedImageFile(null);
    setImageUploading(false);
    setImageUploadError("");
  }, [product, open]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Capitalize product names and descriptions
    let processedValue = value;
    if (
      name === "short_name" ||
      name === "long_name" ||
      name === "description" ||
      name === "group_description" ||
      name === "subgroup_description"
    ) {
      processedValue = value.toUpperCase();
    }

    setFormData({
      ...formData,
      [name]: name === "stock" ? parseInt(value, 10) || 0 : processedValue,
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handlePriceRangeChange = (
    index: number,
    field: keyof PriceRange,
    value: string
  ) => {
    const updatedRanges = [...formData.price_ranges];

    // Parse value as float for min and max to support decimals
    let parsedValue: number;

    if (field === "price") {
      parsedValue = parseInt(value, 10);
      // Handle field-specific validation for price
      parsedValue = parsedValue < 0 ? 0 : parsedValue;
    } else {
      // For min and max fields, parse as float and round to 1 decimal
      parsedValue = parseFloat(value);
      if (isNaN(parsedValue)) {
        parsedValue = 0;
      } else {
        // Round to 1 decimal place
        parsedValue = Math.round(parsedValue * 10) / 10;
      }

      // Handle field-specific validation
      if (field === "min") {
        // Allow min to be 0 (inclusive range)
        parsedValue = parsedValue < 0 ? 0 : parsedValue;

        // If this isn't the first range, ensure min is greater than or equal to the previous max + 0.1
        if (index > 0) {
          const prevMax = updatedRanges[index - 1].max;
          parsedValue = Math.max(parsedValue, prevMax + 0.1);
        }
      } else if (field === "max") {
        const currMin = updatedRanges[index].min;
        // Allow max to be equal to min (for exact quantity pricing)
        parsedValue = parsedValue < currMin ? currMin : parsedValue;

        // If this isn't the last range, ensure max is less than the next min
        if (index < updatedRanges.length - 1) {
          const nextMin = updatedRanges[index + 1].min;
          parsedValue = Math.min(parsedValue, nextMin - 0.1);
        }
      }
    }

    updatedRanges[index] = {
      ...updatedRanges[index],
      [field]: parsedValue,
    };

    // Sort ranges by min value to maintain order
    const sortedRanges = [...updatedRanges].sort((a, b) => a.min - b.min);
    setFormData({ ...formData, price_ranges: sortedRanges });
  };

  const addPriceRange = () => {
    const lastRange = formData.price_ranges[formData.price_ranges.length - 1];
    // Add 0.1 instead of 1 to support decimal increments
    const newMin = lastRange.max + 0.1;
    // Make the range cover 100 units as before
    const newMax = Math.round((newMin + 99.9) * 10) / 10;

    setFormData({
      ...formData,
      price_ranges: [
        ...formData.price_ranges,
        { min: newMin, max: newMax, price: lastRange.price },
      ],
    });
  };

  const removePriceRange = (index: number) => {
    if (formData.price_ranges.length > 1) {
      const updatedRanges = formData.price_ranges.filter((_, i) => i !== index);
      setFormData({ ...formData, price_ranges: updatedRanges });
    }
  };

  // Handle image file selection
  const handleImageFileChange = (file: File | null) => {
    setSelectedImageFile(file);
    setImageUploadError("");
  };

  // Handle image URL change (for manual URL input)
  const handleImageUrlChange = (url: string) => {
    setFormData({ ...formData, product_image_url: url });
    // Clear selected file if user is manually entering URL
    if (url && selectedImageFile) {
      setSelectedImageFile(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setImageUploadError("");

    try {
      let finalImageUrl = formData.product_image_url;

      // If there's a selected file, upload it first
      if (selectedImageFile) {
        setImageUploading(true);
        const uploadResult = await uploadProductImage(selectedImageFile);

        if (!uploadResult.success) {
          setImageUploadError(uploadResult.error || "Error al subir la imagen");
          setImageUploading(false);
          return;
        }

        finalImageUrl = uploadResult.imageUrl || "";
        setImageUploading(false);
      }

      // Create updated or new product with appropriate fields
      const productToSave = {
        // If editing, include the ID, otherwise omit it entirely for new products
        ...(product ? { id: product.id } : {}),
        // Include other required fields
        created_at: product?.created_at || new Date().toISOString(),
        ...formData,
        product_image_url: finalImageUrl,
        updated_at: new Date().toISOString(),
      };

      onSave(productToSave as Product);
    } catch (error) {
      console.error("Error during form submission:", error);
      setImageUploadError("Error inesperado al procesar la imagen");
      setImageUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] sm:max-w-[550px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {product ? "Editar Producto" : "Nuevo Producto"}
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-[calc(80vh-180px)] overflow-y-auto py-4 pr-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="product_image">Imagen del Producto <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
              <ImageUpload
                value={formData.product_image_url}
                onChange={handleImageUrlChange}
                onFileChange={handleImageFileChange}
                loading={imageUploading}
                error={imageUploadError}
                disabled={saving || imageUploading}
                placeholder="Subir imagen del producto"
              />

              {/* Optional: Keep URL input as an alternative */}
              <div className="mt-2">
                <Label
                  htmlFor="product_image_url"
                  className="text-sm text-gray-600"
                >
                  O ingresa una URL de imagen
                </Label>
                <Input
                  id="product_image_url"
                  name="product_image_url"
                  placeholder="https://ejemplo.com/imagen.jpg"
                  value={formData.product_image_url}
                  onChange={handleInputChange}
                  disabled={saving || imageUploading}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="short_name" className="required">
                  Nombre Corto
                </Label>
                <Input
                  id="short_name"
                  name="short_name"
                  placeholder="Ej: Cable HDMI"
                  value={formData.short_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="stock">
                  Stock Disponible <span className="text-muted-foreground font-normal">(Opcional)</span>
                </Label>
                <Input
                  id="stock"
                  name="stock"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.stock}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <Label htmlFor="sku">SKU <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
              <Input
                id="sku"
                name="sku"
                placeholder="Código SKU del producto"
                value={formData.sku}
                onChange={handleInputChange}
              />
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <Label htmlFor="barcode">Código de Barras <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
              <Input
                id="barcode"
                name="barcode"
                placeholder="Código de barras del producto"
                value={formData.barcode}
                onChange={handleInputChange}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="unit_code">Código de Unidad <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
                <Select
                  value={formData.unit_code || ""}
                  onValueChange={(value) =>
                    handleSelectChange("unit_code", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una unidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="base_measurement_unit">Unidad Base <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
                <Select
                  value={formData.base_measurement_unit || ""}
                  onValueChange={(value) =>
                    handleSelectChange("base_measurement_unit", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona unidad base" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="group_code">Código de Grupo <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
                <Input
                  id="group_code"
                  name="group_code"
                  placeholder="Código del grupo"
                  value={formData.group_code}
                  onChange={handleInputChange}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="group_description">Descripción de Grupo <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
                <Input
                  id="group_description"
                  name="group_description"
                  placeholder="Descripción del grupo"
                  value={formData.group_description}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="subgroup_code">Código de Subgrupo <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
                <Input
                  id="subgroup_code"
                  name="subgroup_code"
                  placeholder="Código del subgrupo"
                  value={formData.subgroup_code}
                  onChange={handleInputChange}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="subgroup_description">
                  Descripción de Subgrupo <span className="text-muted-foreground font-normal">(Opcional)</span>
                </Label>
                <Input
                  id="subgroup_description"
                  name="subgroup_description"
                  placeholder="Descripción del subgrupo"
                  value={formData.subgroup_description}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="weight_kg">Peso (kg) <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
                <Input
                  id="weight_kg"
                  name="weight_kg"
                  type="number"
                  min="0"
                  step="0.001"
                  placeholder="0.000"
                  value={formData.weight_kg || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="volume_cm3">Volumen (cm³) <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
                <Input
                  id="volume_cm3"
                  name="volume_cm3"
                  type="number"
                  min="0"
                  step="0.001"
                  placeholder="0.000"
                  value={formData.volume_cm3 || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="visible_to_sellers"
                  checked={formData.visible_to_sellers}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange(
                      "visible_to_sellers",
                      checked as boolean
                    )
                  }
                />
                <Label
                  htmlFor="visible_to_sellers"
                  className="text-sm font-medium"
                >
                  Visible para vendedores
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Si está marcado, este producto será visible para los vendedores
                al crear solicitudes de venta
              </p>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <Label htmlFor="discount_percent">
                Descuento por Defecto (%) <span className="text-muted-foreground font-normal">(Opcional)</span>
              </Label>
              <Input
                id="discount_percent"
                name="discount_percent"
                type="number"
                min="0"
                max="100"
                step="1"
                placeholder="0"
                value={formData.discount_percent ?? 0}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setFormData({
                    ...formData,
                    discount_percent: isNaN(val)
                      ? 0
                      : Math.min(100, Math.max(0, val)),
                  });
                }}
              />
              <p className="text-xs text-muted-foreground">
                Este descuento se aplicará automáticamente al agregar el
                producto a una nota de venta.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="long_name">Nombre Completo <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
              <Input
                id="long_name"
                name="long_name"
                placeholder="Ej: Cable HDMI 2.0 Alta Velocidad 4K"
                value={formData.long_name}
                onChange={handleInputChange}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Descripción <span className="text-muted-foreground font-normal">(Opcional)</span></Label>
              <Textarea
                id="description"
                name="description"
                placeholder="Descripción detallada del producto"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
              />
            </div>

            <div className="mt-2 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <Label>Rangos de Precio</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addPriceRange}
                  className="h-8"
                >
                  <PlusIcon className="mr-1 h-3.5 w-3.5" />
                  Agregar Rango
                </Button>
              </div>

              <div className="space-y-3">
                {formData.price_ranges.map((range, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="grid flex-1 grid-cols-3 gap-2">
                      <div>
                        <Label htmlFor={`min-${index}`} className="text-xs">
                          Desde (unidades)
                        </Label>
                        <Input
                          id={`min-${index}`}
                          value={range.min}
                          onChange={(e) =>
                            handlePriceRangeChange(index, "min", e.target.value)
                          }
                          type="number"
                          min="0"
                          step="0.1"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`max-${index}`} className="text-xs">
                          Hasta (unidades)
                        </Label>
                        <Input
                          id={`max-${index}`}
                          value={range.max}
                          onChange={(e) =>
                            handlePriceRangeChange(index, "max", e.target.value)
                          }
                          type="number"
                          min={range.min}
                          step="0.1"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`price-${index}`} className="text-xs">
                          Precio por unidad
                        </Label>
                        <Input
                          id={`price-${index}`}
                          value={range.price}
                          onChange={(e) =>
                            handlePriceRangeChange(
                              index,
                              "price",
                              e.target.value
                            )
                          }
                          type="number"
                          min="0"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    {formData.price_ranges.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePriceRange(index)}
                        className="mt-5 h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || imageUploading}>
              {saving || imageUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {imageUploading
                    ? "Subiendo imagen..."
                    : product
                      ? "Actualizando..."
                      : "Creando..."}
                </>
              ) : product ? (
                "Actualizar Producto"
              ) : (
                "Crear Producto"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
